<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { __, generateElementId, convertSizeToReadableFormat, getLanguageDirection } from '@oat-sa-private/ui-core';
    import { Textarea, Icon, RichTextEditor } from '@oat-sa-private/ui-elements';
    import { supportsVerticalFormElement } from '@oat-sa-private/ui-elements/input/writingModeSupport.js';
    import Prompt from '../Prompt.svelte';
    import { getInteractionStateStore } from '../../itemsStateStore';
    import { getItemSessionStatusStore } from '../../itemsSessionStatusStore.js';
    import { getItemPendingOperationsStore } from '../../itemsPendingOperationsStore.js';
    import itemSessionStatus from '../../itemSessionStatus.js';
    import { hasClass, extractFromClasses, extractDataValueList, extractDataValueBoolean } from '../util/attributes.js';
    import { formatResponseValue, formatInputValue } from '../util/responseType.js';
    import { getRowsValue, getAdditionalSpacing } from '../util/rows.js';
    import { convertPatternMask } from '../util/pattern.js';
    import editorConfigFactory from './editorConfig.js';
    import { getLocale } from '../../util/locale.js';
    import { getContext, tick, onDestroy, untrack } from 'svelte';
    import { DeferredPromise } from '../util/promise.js';
    import { MaxSizeExceededError } from './uploadAdapter.js';
    import { wrapWithLogger, createLastPressedKeyListener } from '../util/analytics.js';
    import { tryParseMaxlength, tryParseMaxWords } from '../../util/patternMask.js';
    import { defaultsDeep } from 'lodash';

    const qtiClass = 'qti-extendedTextInteraction';
    const cardinality = 'single'; // only single is supported now
    const charactersPerLine = 72;
    const formats = Object.freeze({
        plain: 'plain',
        preformatted: 'preformatted',
        xhtml: 'xhtml'
    });
    // QTI class -> prop conversion maps:
    const counterDirectionTypes = Object.freeze({
        up: 'up',
        down: 'down'
    });

    const itemRunnerConfigContext = getContext('itemRunnerConfig') || {};

    let {
        // keys for state store:
        itemIdentifier,
        responseIdentifier,

        disabled = false,
        readonly = false,
        required = false,

        // Response format:
        baseType = 'string',

        // inherited aria attributes:
        role,
        ariaAttrs = {},

        // inherited item-level QTI attributes:
        language,
        id,
        classes = '',
        dir,
        base = 10,

        // interaction-level QTI attributes:
        expectedLength,
        expectedLines,
        placeholderText,
        prompt,
        format,

        // for image upload, from attributes override
        uploadTimeout = 60 * 1000, //1 min
        uploadMaxSize = 20 * 1000 * 1000, //20Mb
        uploadServiceType, //empty for default or 'sandbox' for no backend

        // for math editor, from attributes override
        mathEditor = {
            provider: null
        },

        dataAttrs = {},
        toolbarPresets = {},
        // tenant config
        spellCheckConfig = {},
        specialCharactersConfig,

        // Persistent Undo/Redo history
        historyLimit = 10,
        usePersistentUndoRedo = false,

        patternMask
    } = $props();

    // svelte-ignore non_reactive_update
    let qtiPatternMaskMessage,
        resizable,
        hasWordCount,
        hasCharCount,
        hasCharCountForExpectedLength,
        hasCharCountForMaxLength,
        hasMathEntry,
        mathEntryKeyboards,
        hasImageUpload,
        removePlugins,
        toolbarRemoveItems,
        maxCharLimit,
        specialCharacterSetName,
        enforceMaxWords,
        expectedResponseLength,
        maxlength,
        maxWordsLimit,
        maxWordsSeparators,
        rows,
        counterDirection;

    setDerivedProps();

    /**
     * Ignore state_referenced_locally: in interaction, props do not change, so derived values don't need to be reactive.
     */
    function setDerivedProps() {
        // data attributes
        qtiPatternMaskMessage = dataAttrs['data-patternmask-message'];
        resizable = dataAttrs['data-resizable'] !== 'false';
        hasWordCount = extractDataValueBoolean(dataAttrs['data-word-count']);
        hasCharCount = extractDataValueBoolean(dataAttrs['data-character-count']);
        hasCharCountForExpectedLength = extractDataValueBoolean(dataAttrs['data-character-count-expected'], true);
        hasCharCountForMaxLength = extractDataValueBoolean(dataAttrs['data-character-count-max'], true);
        // xhtml-only options
        hasMathEntry = extractDataValueBoolean(dataAttrs['data-math-entry']);
        mathEntryKeyboards = dataAttrs['data-math-entry-keyboards'];
        hasImageUpload = extractDataValueBoolean(dataAttrs['data-image-upload']);
        removePlugins = extractDataValueList(dataAttrs['data-remove-plugins']);
        toolbarRemoveItems = extractDataValueList(dataAttrs['data-toolbar-remove-items']);

        // if plain or preformatted format, maxCharLimit applies to total response characters
        // if xhtml format, maxCharLimit applies to only visible characters, as reported in the count
        const charBoundary = 20000;
        maxCharLimit = Number.isInteger(dataAttrs['data-max-chars']) ? dataAttrs['data-max-chars'] : charBoundary;
        specialCharacterSetName = dataAttrs['data-special-characters'];
        enforceMaxWords = hasClass(classes, 'tao-constrain-maxWords');

        // expected length
        expectedResponseLength = expectedLength || expectedLines * charactersPerLine || null;

        //patternMask & maxlength validation &  max words validation
        if (patternMask) {
            patternMask = convertPatternMask(patternMask);
            maxlength = tryParseMaxlength(patternMask);
            if (maxlength) {
                if (maxlength > maxCharLimit) {
                    maxCharLimit = maxlength;
                }
                patternMask = null;
            } else {
                const maxWordsParseResult = tryParseMaxWords(patternMask);
                if (maxWordsParseResult) {
                    maxWordsLimit = maxWordsParseResult.max;
                    maxWordsSeparators = maxWordsParseResult.separators;
                    patternMask = null;
                }
            }
        }

        // interaction-level QTI attributes derived from classes:
        rows = getRowsValue({ expectedLength, expectedLines, maxlength, maxWordsLimit, classes });
        counterDirection =
            extractFromClasses(classes, 'qti-counter-', val => counterDirectionTypes[val]) || counterDirectionTypes.up;
    }

    // Element refs
    let interactionElement;
    let feedbacksRootRef;
    let controlWrapperRef;

    let editorToolbarHeight = $state(0);
    let feedbacksHeight = $state(0);
    let additionalSpace = $state(0);

    const interactionName = 'extendedTextInteraction';

    const hiddenEditorLabel = __('Your answer');

    /**
     * Mapping for RichTextEditor prop editorType
     * Settable by:
     * 1. authoring the data-attr into the item QTI
     * 2. tenant config (propertyOverride) setting dataAttrs (overrides #1)
     * 3. LTI claim (goes into itemRunnerConfigContext) (overrides #2)
     * @type {String} 'classic' (default) or 'document'
     */
    const editorType = (function () {
        const itemDataVal = dataAttrs['data-editor-type'];
        // set by LTI
        const configuredVal = itemRunnerConfigContext.elements?.ExtendedTextInteraction?.editorType;
        return typeof configuredVal !== 'undefined' ? configuredVal : itemDataVal;
    })();

    /**
     * Mapping for CKEditor config property toolbar.shouldNotGroupWhenFull
     * Settable by:
     * 1. authoring the data-attr into the item QTI
     * 2. tenant config (propertyOverride) setting dataAttrs (overrides #1)
     * 3. LTI claim (goes into itemRunnerConfigContext) (overrides #2)
     * @type {Boolean} if toolbar should NOT group overflowing items (default: true, items not grouped)
     */
    const toolbarShouldNotGroupWhenFull = (function () {
        const itemDataVal = extractDataValueBoolean(dataAttrs['data-toolbar-should-not-group-when-full'], true);
        // set by LTI
        const configuredVal = itemRunnerConfigContext.elements?.ExtendedTextInteraction?.toolbarShouldNotGroupWhenFull;
        return typeof configuredVal !== 'undefined' ? configuredVal : itemDataVal;
    })();

    /**
     * Mapping for CKEditor config property toolbar.items
     * - toolbarPresets settable by tenant config (via propertyOverride of toolbarPresets)
     * - toolbarPreset settable by LTI claim (via itemRunnerConfigContext)
     * @type {Object} toolbarPreset
     * @property {String[]} items - maps to editorConfig.toolbar.items
     */
    const toolbarPreset = (function () {
        const presetName = itemRunnerConfigContext.elements?.ExtendedTextInteraction?.toolbarPreset;
        return toolbarPresets[presetName] || {};
    })();

    /**
     * spellCheckConfig should ultimately be settable by:
     * 1. tenant config (via propertyOverride of spellCheckConfig) - especially for url, licence etc.
     * 2. LTI claim (via itemRunnerConfigContext) - for customisation by launcher
     * @type {Object} spellCheckConfig
     * @property {Boolean} enabled
     * @property {String} providerId
     * @property {Object} providerConfig - specific to the provider
     */
    const finalSpellCheckConfig = (function () {
        // LTI claim
        const configuredSpellCheckConfig =
            itemRunnerConfigContext.elements?.ExtendedTextInteraction?.spellCheckConfig || {};
        return defaultsDeep({}, configuredSpellCheckConfig, spellCheckConfig, {
            enabled: true,
            providerId: 'native'
        });
    })();

    let storedEditorHistory = $state([]);

    // svelte-ignore state_referenced_locally
    const itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier),
        interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        pendingOperationsStore = getItemPendingOperationsStore(itemIdentifier);

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const logger = itemContext && itemContext.getLogger();
    const userLang = itemContext && itemContext.getUserLang();
    const itemLang = itemContext && itemContext.getItemLang();
    const instructionsLang = itemContext && itemContext.getInstructionsLang();
    const instructionsDir = instructionsLang ? getLanguageDirection(instructionsLang) : void 0;
    const writingMode = itemContext && itemContext.getWritingMode();
    const isVerticalWritingMode = writingMode === 'vertical-rl';
    const isVerticalTextareaSupported = supportsVerticalFormElement();

    let visited = $state(false);

    // svelte-ignore state_referenced_locally
    // do initial response definition (QTI props are fixed for the mount lifetime)
    if (!interactionStateStore.hasResponse()) {
        interactionStateStore.merge({ qtiClass });
        interactionStateStore.setResponseValue(
            {
                cardinality,
                baseType,
                value: null
            },
            // validity:
            !required && (!patternMask || new RegExp(patternMask).test(''))
        );
    } else {
        visited = true;
    }

    // subscribe to store value
    let value = $derived.by(() => {
        if ($interactionStateStore) {
            return untrack(() => loadResponse());
        } else {
            return '';
        }
    });

    let notificationKeys = [];
    const lastPressedKeyListener = createLastPressedKeyListener();

    /**
     * Tell the host Item to remove all Notifications added by this interaction
     */
    function removeItemNotifications() {
        for (const key of notificationKeys) {
            itemContext.removeItemNotification(key);
        }
        notificationKeys = [];
    }

    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const finishedUploadKeys = new Set();

    // extend config template based on interaction data
    function getEditorConfig() {
        return editorConfigFactory({
            toolbarItems: toolbarPreset?.items,
            removePlugins,
            toolbarRemoveItems,
            toolbarShouldNotGroupWhenFull,
            toolbarLang: instructionsLang || userLang,
            inputLang: language || itemLang || userLang,
            hasMathEntry,
            isWirisMathEditorEnabled: mathEditor.provider === 'wiris',
            mathEntryKeyboards,
            hasImageUpload,
            specialCharacterSetName,
            specialCharactersConfig,
            spellCheckConfig: finalSpellCheckConfig,
            uploadServiceType,
            uploadTimeout,
            uploadMaxSize,
            responseIdentifier,
            itemIdentifier,
            getAttachmentsUploadData:
                itemContext.getGetAttachmentsUploadData && itemContext.getGetAttachmentsUploadData(),
            onUploadStarted: uploadKey => {
                //register pending operation in the store, in case consumer wants to wait for upload completion
                pendingOperationsStore.add(uploadKey);
            },
            onUploadFinished: (uploadKey, success, error) => {
                //NB! Do not unmount svelte component until this completes!
                //response will be updated only on next `change` event, so unregister global pending operation after that
                finishedUploadKeys.add(uploadKey);

                if (error && error.name !== 'AbortError') {
                    let uploadErrorMessage;
                    if (error instanceof MaxSizeExceededError) {
                        uploadErrorMessage = __(
                            'Your image file is too big. Please insert an image no larger than %s',
                            convertSizeToReadableFormat(uploadMaxSize)
                        );
                    } else {
                        uploadErrorMessage = __('Your image failed to upload, please try again');
                        if (logger) {
                            logger.error(error);
                        }
                    }
                    notificationKeys.push(
                        itemContext.showItemNotification(
                            {
                                message: uploadErrorMessage,
                                hierarchy: 'alert',
                                closeable: true
                            },
                            'persistent'
                        )
                    );
                }
            }
        });
    }

    const editorConfig = getEditorConfig();

    // handle char/word counter
    let count = $state({ words: 0, chars: 0 });

    // error handling
    const elementId = generateElementId(interactionName);
    const editorElementId = generateElementId('extendedText-editor');
    let customValidity = $state('');
    let validity = $state({
        store: true, // 'true' value allow avoiding warning messages on initial loading, however itemStore contains actual validity state
        wrongFormatting: false,
        maxWordsExceeded: false,
        valid: true
    });
    /**
     * Used for validation with pattern. Always resolves with boolean value
     * @resolves Boolean
     */
    let validationDeferred;

    // Due to async subcomponents, register a loading Promise with the Item
    let interactionReady = $state(false);
    const editorLoad = new DeferredPromise();
    itemContext.registerLoadingElement(editorLoad.promise);
    const initialValue = $interactionStateStore ? loadResponse() : '';

    /**
     * Screen-reader feedback properties:
     * - set ariaLive: 'assertive' for instant announcement
     * - set ariaLive: 'polite' for deferred announcement
     */
    let feedbacks = $derived([
        {
            message: __('Maximum characters reached (maximum: %d)', maxlength || maxCharLimit),
            permanent: count?.maxCharLimitExceeded || count?.maxLengthLimitExceeded,
            error: count?.maxCharLimitExceeded || count?.maxLengthLimitExceeded,
            ariaLive: 'assertive'
        },
        {
            message: __('Maximum words reached (maximum: %d)', maxWordsLimit),
            permanent: count?.maxWordsExceeded,
            error: count?.maxWordsExceeded,
            ariaLive: 'assertive'
        },
        // the following 2 are mutually exclusive: tao-constrain-maxWords and data-word-count shouldn't be enabled together
        {
            message: __('<strong>%d</strong> / %d word(s) typed', count?.words || 0, maxWordsLimit),
            permanent: maxWordsLimit,
            error: false
        },
        {
            message: __('<strong>%d</strong> word(s) typed', count?.words || 0),
            permanent: hasWordCount && !maxWordsLimit,
            error: false
        },
        {
            message: __('<strong>%d</strong> character(s) typed', count?.chars || 0),
            permanent: hasCharCount && !maxlength && !expectedResponseLength,
            error: false
        },
        {
            message:
                counterDirection === counterDirectionTypes.up
                    ? `${__('<strong>%d</strong> character(s) typed', count?.chars)} (${__(
                          'recommended: %d',
                          expectedResponseLength
                      )}).`
                    : __('<strong>%d</strong> characters left.', expectedResponseLength - count?.chars),
            permanent: hasCharCountForExpectedLength && expectedResponseLength && !maxlength,
            error: false
        },
        {
            message: `<span aria-label="${__('%d out of %d characters typed', count?.chars || 0, maxlength)}">${__(
                '<strong>%d</strong> / %d character(s) typed',
                count?.chars || 0,
                maxlength
            )}</span>`,
            permanent: hasCharCountForMaxLength && maxlength,
            error: false
        },
        {
            message: qtiPatternMaskMessage || __('Invalid format, please refer to the instructions.'),
            permanent: qtiPatternMaskMessage && validity.patternMismatch,
            error: visited && validity.patternMismatch,
            ariaLive: 'polite'
        },
        {
            message: __('Answer required.'),
            permanent: required,
            error: visited && validity.valueMissing,
            ariaLive: 'polite'
        }
    ]);

    /**
     * Tells if a given feedback should be invisible
     * @param {Object} feedback
     * @returns {Boolean}
     */
    function feedbackInvisible(feedback) {
        return feedback.hide || !(feedback.error || feedback.permanent);
    }

    let visibleFeedbacks = $derived(feedbacks.filter(feedback => !feedbackInvisible(feedback)));

    /**
     * Loads state (for word count & validity) from interaction store
     * Loads response from interaction store and formats it
     * @returns {string} Response in the store
     */
    function loadResponse() {
        const state = interactionStateStore.get();
        if (state && state.count) {
            count = state.count;
        }

        if (state && state.history) {
            storedEditorHistory = state.history;
        }

        validity.overallValidity = state.validity;

        const response = interactionStateStore.getResponseValue();

        const storedValue = formatInputValue(response, baseType, base, getLocale());

        return storedValue.toString();
    }

    /**
     * Change event handler
     * - parses new response value (according to locale) from subcomponent event
     * - reads wordcount object from subcomponent event
     * - validates formatting edge case
     * - stores the response
     * @param {CustomEvent} e
     * @param {Object} e.detail
     * @param {String} e.detail.value
     * @param {Object?} [e.detail.count]
     * @param {Number?} [e.detail.count.words]
     * @param {Number?} [e.detail.count.chars]
     */
    function handleChange(e) {
        let parsedValue = null;
        let { value: newValue } = e.detail;
        count = e.detail.count || {};
        count.maxCharLimitExceeded = maxCharLimit && count?.chars && count?.chars > maxCharLimit;
        count.maxLengthLimitExceeded = maxlength && count?.chars && count?.chars > maxlength;
        count.maxWordsExceeded = maxWordsLimit && count?.words && count?.words > maxWordsLimit;

        validity.maxWordsExceeded = count.maxWordsExceeded; //count: to show feedback after restoring existing itemState; validity: to store with response

        if (patternMask && format !== formats.xhtml) {
            validationDeferred = new DeferredPromise();
        }

        // format value
        validity.wrongFormatting = false;
        try {
            parsedValue = formatResponseValue(newValue, baseType, base, getLocale());
        } catch (error) {
            if (error instanceof TypeError) {
                validity.wrongFormatting = true;
                if (validationDeferred) {
                    // resolve as invalid validity
                    validationDeferred.promise.then(() => {
                        interactionStateStore.setValidity(false);
                    });
                    validationDeferred.resolve();
                }
            }
        }
        /* Rich text editor dispatches `validation` and then `change`
         * that's why item store is not recieving maxWordsExceeded validity on time
         * validity need to be synced here to expose it to `navigation` in a correct state */
        syncValidity(newValue);
        storeResponse(parsedValue);
    }

    /**
     * Validation event handler
     * @param {CustomEvent} e
     */
    function handleValidation(e) {
        if (validationDeferred) {
            validationDeferred.promise.then(() => {
                syncValidity(e.detail);
                interactionStateStore.setValidity(e.detail.valid);
            });
            validationDeferred.resolve();
        } else {
            syncValidity(e.detail);
            let storeValue = interactionStateStore.getResponseValue() || null;
            storeResponse(storeValue);
        }
    }

    /**
     * Sync validity to show correct feedback
     * @param {Object} validityResponse response from validation event
     */
    function syncValidity(validityResponse) {
        validity = Object.assign({}, validity, validityResponse);
        validity.overallValidity = !validity.wrongFormatting && !validity.maxWordsExceeded && validity.valid; //actualize validity store
        if (visited) {
            // set component to invalid state
            customValidity = validity.overallValidity ? '' : 'invalid';
        }
    }

    /**
     * Store value in interactionStateStore
     *
     * validity of this component consists of two parts: validity from 'validation' event and parsing value in 'change' event
     * for getting correct validation state we have to collect info from two events, but we never know in which order it will come
     * variable `storeValue` is used to say: Have we collected data from both events or not
     * @param {string|null} parsedValue - value prepared for adding to store
     */
    function storeResponse(parsedValue) {
        // Note: subscribers to interactionStateStore will see 2 changes back-to-back
        // because of these next calls. (It would be better to do only 1 call.)
        // Response is stored before state is updated because it's the more important part.

        interactionStateStore.setResponseValue(
            {
                cardinality,
                baseType,
                value: parsedValue
            },
            validationDeferred ? validationDeferred.promise : validity.overallValidity
        );
        interactionStateStore.update({
            count: $state.snapshot(count)
        });

        // only now is it safe to delete pendingOperations entries of finished uploads
        finishedUploadKeys.forEach(uploadKey => {
            pendingOperationsStore.delete(uploadKey);
        });
    }

    /**
     * Runs on subcomponent 'ready' event
     * @param {CustomEvent} e
     * @param {Object} e.detail
     * @param {Object?} [e.detail.count]
     * @param {Number?} [e.detail.count.words]
     * @param {Number?} [e.detail.count.chars]
     */
    function handleEditorReady(e) {
        editorLoad.resolve();
        interactionReady = true;

        if (e.detail?.count) {
            count = e.detail.count;
        }

        /**
         * RichTextEditor not rendered fully at this moment
         * which results in the wrong toolbar height on first render on mobile
         */
        tick().then(() => {
            feedbacksHeight = getFeedbacksRootHeight();
            updateKeyHeights();
        });
    }

    /**
     * Runs if the editor component had an error, either on mount or later.
     * In certain cases the item state should be closed (requiring a reload), therefore we inform the Item
     * @param {CustomEvent} e
     */
    function handleEditorError(e) {
        const editorError = e.detail.error;
        if (editorError.message.includes('WEBSPELLCHECKER')) {
            editorError.recoverable = true;
        }
        if (!interactionReady) {
            // For cases where the Promise was not resolved first
            editorLoad.reject(editorError);
        } else {
            // For async errors, e.g. lazy loading
            itemContext.triggerError(editorError);
        }
    }

    /**
     * Runs if the editor component wants to show a notification.
     * The host Item will handle the creation.
     * @param {CustomEvent} e
     */
    function handleEditorNotify(e) {
        const [notification, persistence, timeout] = e.detail;

        if (notification && (notification.title || notification.message)) {
            notificationKeys.push(itemContext.showItemNotification(notification, persistence, timeout));
        }
    }

    /**
     * Gets rich text editor toolbar height (if present)
     * @returns {Number}
     */
    function getEditorToolbarHeight() {
        const editorToolbar = controlWrapperRef?.querySelector('.ck-editor__top');
        if (!editorToolbar) {
            return 0;
        }
        return isVerticalWritingMode ? editorToolbar.clientWidth : editorToolbar.clientHeight;
    }

    /**
     * Gets feedbacks height
     * @returns {Number}
     */
    function getFeedbacksRootHeight() {
        const clientBlockSize = isVerticalWritingMode ? feedbacksRootRef?.clientWidth : feedbacksRootRef?.clientHeight;
        return clientBlockSize || 0;
    }

    /**
     * Updates values needed for control height calculation
     */
    function updateKeyHeights() {
        editorToolbarHeight = getEditorToolbarHeight();
        if (interactionElement) {
            additionalSpace = getAdditionalSpacing(interactionElement, !!prompt, isVerticalWritingMode);
        }
    }

    function getInteractionElement() {
        return interactionElement;
    }

    const handleFocus = wrapWithLogger({
        interactionStateStore,
        getInteractionElement,
        getDetails() {
            return {
                format
            };
        }
    });

    const handleBlur = wrapWithLogger({
        interactionStateStore,
        getInteractionElement,
        getDetails() {
            return {
                newResponse: interactionStateStore.getResponseValue(),
                format
            };
        }
    });

    /**
     * Logs clipboard events
     */
    const handleClipboard = wrapWithLogger({
        interactionStateStore,
        getInteractionElement,
        handler() {
            return new Promise(resolve => {
                setTimeout(resolve, 0);
            });
        },
        getDetails(event) {
            const { content, replacedContent, position } = event.detail;

            return {
                content,
                replacedContent,
                position,
                format,
                pressedKey: lastPressedKeyListener.lastPressedKey
            };
        }
    });

    /**
     * Logs drag and drop event
     */
    const handleDragDropEvent = wrapWithLogger({
        interactionStateStore,
        getInteractionElement,
        handler() {
            return new Promise(resolve => {
                setTimeout(resolve, 0);
            });
        },
        getDetails(event) {
            const { content, position } = event.detail;

            return {
                content,
                position,
                format
            };
        }
    });

    /**
     * Stores editor history when RichTextEditor provides it
     * @param {CustomEvent} event
     */
    const handleHistoryUpdate = event => {
        interactionStateStore.update({
            history: event?.detail
        });
    };

    /* temp readonly override ===== */
    const richTextReadonlyLockId = Symbol('RichTextReadonlyLock');
    let richTextReadonly = $derived(
        Boolean($itemSessionStatusStore === itemSessionStatus.closed || readonly || disabled)
    );
    $effect(() => {
        if (interactionReady) {
            const editorInstance = controlWrapperRef?.querySelector('.ck-editor__editable')?.ckeditorInstance;
            if (editorInstance && editorInstance.isReadOnly !== richTextReadonly) {
                if (richTextReadonly) {
                    editorInstance.enableReadOnlyMode(richTextReadonlyLockId);
                } else {
                    editorInstance.disableReadOnlyMode(richTextReadonlyLockId);
                }
            }
        }
    });
    /* ============================ */

    onDestroy(() => {
        removeItemNotifications();
    });
</script>

<style>
    .feedbacks {
        margin-inline: 0;
        margin-block: var(--feedbacks-margin-top) 0;
        padding: 0;
        list-style: none;
    }

    .feedback {
        color: var(--color-text-feedback);

        &.error {
            color: var(--color-alert);
        }
        & .bullet {
            display: inline-block;
            text-align: center;
            min-inline-size: 2rem;
            margin-inline-end: var(--space-1x);
        }
    }

    [aria-controls] {
        --feedbacks-margin-top: 0px;
        --base-control-height: min(var(--item-container-inner-block-size), 150rem);
        --row-height: calc(var(--line-height-default) * 1em);
        --maximal-visible-height: calc(var(--item-container-block-size, 80vh) - var(--toolbar-height, 0) - 2rem);

        cursor: auto;

        &.has-feedbacks {
            --feedbacks-margin-top: var(--space-2x);
        }

        &.auto-height {
            & :global(.cke-wrapper) {
                --content-height: calc(var(--control-default-height) - var(--border-medium));
            }

            & :global(textarea) {
                block-size: var(--control-default-height);
                min-block-size: calc(var(--row-height) * 3 + 3rem);
                vertical-align: top;
            }

            & :global(.document-editor) {
                & :global(.ck.ck-editor__main) {
                    block-size: var(--control-default-height);
                    min-block-size: calc(var(--row-height) + 15rem);
                }
            }
        }
        /* For better UX, make editor content area fit on screen without scrolling item */
        & :global(.classic-editor .ck.ck-content),
        & :global(textarea) {
            max-block-size: var(--maximal-visible-height);
        }
        & :global(.document-editor) {
            & :global(.ck.ck-editor__main) {
                max-block-size: var(--maximal-visible-height);
                & :global(.ck.ck-content) {
                    min-block-size: calc(var(--row-height) * var(--rows-count) - 9rem);
                    block-size: fit-content;
                }
            }
        }
        /* document-editor, when rows is undefined or zero or set high enough, should expand its empty page */
        &.auto-height,
        &.grow-empty-document {
            & :global(.document-editor) {
                & :global(.ck.ck-editor__main) {
                    & :global(.ck.ck-content) {
                        min-block-size: calc(0.9 * var(--maximal-visible-height));
                    }
                }
            }
        }
    }

    :global(.writing-mode-vertical-rl) {
        & [aria-controls] {
            --row-height: calc(var(--line-height-default-ruby) * 1em);
            margin-block-end: 0.25rem;

            & :global(textarea) {
                inline-size: 100%;
                line-height: var(--line-height-default-ruby);
            }

            &.vertical-unsupported {
                & :global(textarea) {
                    writing-mode: horizontal-tb;
                    height: 100%;
                    min-height: 100%;
                    /*if number of rows is set: */
                    width: calc(var(--row-height) * var(--rows-count) + 3rem);
                    min-width: calc(var(--row-height) * 1 + 3rem);
                }

                &.auto-height {
                    & :global(textarea) {
                        width: var(--control-default-height);
                        min-width: calc(var(--row-height) * 3 + 3rem);
                    }
                }

                & :global(.resize-vertical.resize-vertical) {
                    resize: horizontal;
                }
            }
        }
    }

    @media screen and (--mq-minwidth-medium) and (--mq-maxwidth-huge) {
        :global([data-layouts~='dualColumnLayout']) [aria-controls] {
            --base-control-height: var(--grid-row-inner-height);
        }
    }

    :global([data-layouts~='hideFeedbacksLayout']) {
        & .feedbacks {
            display: none;
        }
    }
</style>

<svelte:window onresize={updateKeyHeights} />
<div
    data-format={format}
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    bind:this={interactionElement}
    lang={language}
    {id}
    {dir}
    {role}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if prompt && prompt.length}
        <Prompt blockTree={prompt} />
    {/if}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
        class:do-not-read={!value}
        bind:this={controlWrapperRef}
        style={`
            --control-default-height: calc(
                var(--base-control-height) - ${
                    editorToolbarHeight + feedbacksHeight
                }px - ${additionalSpace} - var(--feedbacks-margin-top)
            );
            --rows-count: ${rows};
            --toolbar-height: ${editorToolbarHeight}px;
        `}
        aria-controls={elementId}
        class:has-feedbacks={visibleFeedbacks.length > 0}
        class:auto-height={!rows}
        class:grow-empty-document={editorType === 'document' && rows > 15}
        class:vertical-unsupported={!isVerticalTextareaSupported}
        onkeydown={lastPressedKeyListener.saveLastPressedKey}>
        {#if format === formats.xhtml}
            <RichTextEditor
                {editorType}
                {rows}
                minRows={rows ? 1 : 3}
                value={initialValue}
                {storedEditorHistory}
                {historyLimit}
                {usePersistentUndoRedo}
                placeholder={placeholderText}
                {required}
                maxlength={maxlength || maxCharLimit}
                resizable={resizable ? 'vertical' : 'none'}
                {editorConfig}
                ariaLabel={hiddenEditorLabel}
                spellcheck={finalSpellCheckConfig.enabled}
                bind:visited
                on:notify={handleEditorNotify}
                on:error={handleEditorError}
                on:ready={handleEditorReady}
                on:change={handleChange}
                on:validation={handleValidation}
                on:focus={handleFocus}
                on:blur={handleBlur}
                on:clipboard={handleClipboard}
                on:dragdrop={handleDragDropEvent}
                on:historyUpdate={handleHistoryUpdate} />
        {:else}
            <Textarea
                id={editorElementId}
                {rows}
                {value}
                placeholder={placeholderText}
                {disabled}
                readonly={$itemSessionStatusStore === itemSessionStatus.closed || readonly}
                {required}
                maxlength={maxlength || maxCharLimit}
                maxlengthAttrEnabled={!!maxlength}
                resizable={resizable ? 'vertical' : 'none'}
                {customValidity}
                fullwidth
                hiddenLabel={hiddenEditorLabel}
                pattern={patternMask}
                wordSeparators={maxWordsSeparators}
                maxWords={enforceMaxWords && maxWordsLimit}
                spellcheck={finalSpellCheckConfig.enabled}
                bind:visited
                on:ready={handleEditorReady}
                on:change={handleChange}
                on:validation={handleValidation}
                on:focus={handleFocus}
                on:blur={handleBlur}
                on:dragdrop={handleDragDropEvent}
                on:clipboard={handleClipboard} />
        {/if}
        <label for={editorElementId}>
            <ul
                bind:this={feedbacksRootRef}
                id={elementId}
                class="feedbacks do-not-read"
                lang={instructionsLang}
                dir={instructionsDir}>
                <!-- eslint-disable-next-line svelte/require-each-key -->
                {#each visibleFeedbacks as feedback}
                    <li class="feedback" class:error={feedback.error} aria-live={feedback.ariaLive}>
                        {#if visibleFeedbacks.length > 1 || feedback.error}
                            <span class="bullet" aria-hidden="true">
                                {#if feedback.error}
                                    <Icon name="warning-16" ariaHidden />
                                {:else}•{/if}
                            </span>
                        {/if}
                        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                        <span>{@html feedback.message}</span>
                    </li>
                {/each}
            </ul>
        </label>
    </div>
</div>
